import {
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild,
} from "@angular/core";
import { Observable, of, Subject } from "rxjs";
import { first } from "rxjs/operators";
import { ToastService } from "src/app/shared/toast.service";
import { Person } from "../../../../shared/Models/read/Person";
import { OutsideSurveyService } from "../../../../shared/services/outside-survey.service";
import { ContactFormComponent } from "../../shared/components/contact-form/contact-form.component";
import { SurveyFrameTabTemplateComponent } from "../../shared/survey-frame-list/survey-frame-tab-template/survey-frame-tab-template.component";
import { OUTSIDE_SURVEY_CONTACTS } from "../config/outside-survey.magic-strings";

@Component({
  selector: "app-outside-survey-contact",
  templateUrl: "./outside-survey-contact.component.html",
  styleUrls: ["./outside-survey-contact.component.scss"],
})
export class OutsideSurveyContactComponent
  implements OnDestroy, OnInit, OnChanges
{
  tabId = OUTSIDE_SURVEY_CONTACTS;
  @Input() contacts: Person[];
  @Input() buildingId: string;
  @Input() isAuthCanWrite: boolean;

  @ViewChild("contactRef")
  contactComp: ContactFormComponent;
  @ViewChild("tabs", { static: true })
  tabComponent: SurveyFrameTabTemplateComponent;

  selectedContact: Person;
  addNewContactScreenActive: boolean;
  selectedSearchResult: boolean;
  contexts: any[] = [];

  get showTabContent() {
    return (
      (!!this.tabComponent.openTab &&
        this.tabComponent.openTab != "new-contact") ||
      (this.addNewContactScreenActive && !!this.selectedContact)
    );
  }

  constructor(
    private outsideSurveyService: OutsideSurveyService,
    private toastService: ToastService
  ) {}

  ngOnInit() {
    this.updateContexts();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.contacts) this.updateContexts();
  }

  destroy$ = new Subject();
  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  toggleNewContact(bool?: boolean) {
    let i = this.contexts.findIndex((c) => c.id == "new-contact");
    let addNewContact = () => {
      this.addNewContactScreenActive = true;
      this.contexts.unshift({
        id: "new-contact",
        disabled: true,
        hidden: false,
        contact: new Person(),
        noContacts: false,
        newContact: true,
      });
      this.tabComponent.onToggleDetail("new-contact", true);
    };
    let removeNewContact = () => {
      this.addNewContactScreenActive = false;
      this.contexts.splice(i, 1);
      this.tabComponent.onToggleDetail(undefined, true);
    };
    if (bool == undefined)
      if (i == -1) addNewContact();
      else removeNewContact();
    else if (bool && i == -1) addNewContact();
    else if (i != -1) removeNewContact();
  }

  updateContexts() {
    this.addNewContactScreenActive = false;
    this.contexts =
      this.contacts?.map((c) => ({
        id: c.id,
        disabled: false,
        hidden: false,
        contact: c,
        noContacts: false,
        newContact: false,
      })) ?? [];
    let i = this.contexts.findIndex((c) => c.id == "no-contact");
    if (this.contacts.length == 0 && i == -1)
      this.contexts.unshift({
        id: "no-contacts",
        disabled: true,
        hidden: this.contacts?.length > 0,
        contact: undefined,
        noContacts: true,
        newContact: false,
      });
    else if (i != -1) this.contexts.splice(i, 1);
    this.tabComponent.onToggleDetail(undefined);
  }

  onRemoveFromListContact(contact) {
    if (!navigator.onLine) {
      this.toastService.showWarnToast(
        $localize`:@@general.nointernet:No internet`,
        $localize`:@@general.nointernet.actionnotpossible:This action is not possible while you're offline.`
      );
      return;
    }

    this.outsideSurveyService
      .removeContactFromList({
        buildingId: this.buildingId,
        personId: contact.id,
      })
      .pipe(first())
      .subscribe((res) => {
        this.selectedContact = null;
        let i = this.contacts.findIndex((c) => c.id == contact.id);
        if (i != -1) this.contacts.splice(i, 1);
        this.updateContexts();
      });
  }

  onEditContact(contact) {
    if (!navigator.onLine) {
      this.toastService.showWarnToast(
        $localize`:@@general.nointernet:No internet`,
        $localize`:@@general.nointernet.actionnotpossible:This action is not possible while you're offline.`
      );
      return;
    }
    let i = this.contacts.findIndex((c) => c.id == contact.id);
    if (i != -1) this.contacts[i] = contact;
    this.updateContexts();
    this.busySaving = false;
  }

  onAddNewContactToList(contact) {
    if (!navigator.onLine) {
      this.toastService.showWarnToast(
        $localize`:@@general.nointernet:No internet`,
        $localize`:@@general.nointernet.actionnotpossible:This action is not possible while you're offline.`
      );
      return;
    }

    if (contact) {
      const exists = this.contacts.some((c) => c.id === contact.id);
      if (!exists) this.contacts.push(contact);
      this.updateContexts();
      this.tabComponent.onToggleDetail(contact.id);
    }
    this.cancelNewContact();
    this.busySaving = false;
  }

  onSelectContact(contact) {
    this.toggleNewContact(false);
    if (this.selectedContact === contact) this.selectedContact = null;
    else this.selectedContact = contact;
  }

  onAddContactBtn() {
    if (!navigator.onLine) {
      this.toastService.showWarnToast(
        $localize`:@@general.nointernet:No internet`,
        $localize`:@@general.nointernet.actionnotpossible:This action is not possible while you're offline.`
      );
      return;
    }

    this.selectedContact = null;
    this.toggleNewContact(true);
    this.tabComponent.onToggleDetail("new-contact");
  }

  onSaveContactToList() {
    if (!navigator.onLine) {
      this.toastService.showWarnToast(
        $localize`:@@general.nointernet:No internet`,
        $localize`:@@general.nointernet.actionnotpossible:This action is not possible while you're offline.`
      );
      return;
    }

    this.busySaving = true;
    if (this.selectedContact.id)
      this.outsideSurveyService
        .addContactToList({
          buildingId: this.buildingId,
          personId: this.selectedContact.id,
        })
        .pipe(first())
        .subscribe((res) => {
          this.selectedContact.buildingId = this.buildingId;
          this.onAddNewContactToList(this.selectedContact);
          this.cancelNewContact();
          this.busySaving = false;
        });
    else this.contactComp.saveNewContactToList(this.buildingId);
  }

  cancelNewContact() {
    this.addNewContactScreenActive = false;
    this.toggleNewContact(false);
    this.selectedSearchResult = false;
    this.selectedContact = null;
  }

  onSelectSearchResult(searchResult: Person) {
    this.selectedContact = searchResult;
    this.selectedSearchResult = true;
    this.contexts.find((c) => c.id == "new-contact").contact = searchResult;
  }

  onNewContact() {
    this.selectedContact = new Person();
  }

  busySaving = false;

  saveContact() {
    if (this.addNewContactScreenActive) {
      if (this.selectedContact.id) {
        this.onSaveContactToList();
      } else {
        if (!this.contactComp.formIsValid) return;

        this.busySaving = true;
        this.contactComp.saveNewContactToList(this.buildingId);
      }
    } else {
      if (!this.contactComp.formIsValid) return;

      this.busySaving = true;
      this.contactComp.onSaveContact();
    }
  }

  deleteContact() {
    this.contactComp.onRemoveFromListContact();
  }

  save(): Observable<boolean> {
    return of(true);
  }
}
